/**
 * Admin · Reports — CSV exports across the five core data streams.
 * Days picker (1-90) shared by all four cards.
 */
import { useState } from "react";
import { Download, FileText } from "lucide-react";
import { api } from "../../lib/api";
import { useAdminWorkspace, AdminHeader, AdminEmpty } from "./_shared";

const KINDS = [
  {
    kind: "attendance",
    title: "Attendance log",
    blurb: "Every sign-in and sign-out, with pickup person and staff note.",
  },
  {
    kind: "pickups",
    title: "Pickup log",
    blurb: "Active and resolved arrival requests from parents.",
  },
  {
    kind: "announcements",
    title: "Communication log",
    blurb: "Calm messages sent to families, grouped or center-wide.",
  },
  {
    kind: "absences",
    title: "Absence report",
    blurb: "Days each family let you know quietly.",
  },
  {
    kind: "status-history",
    title: "Status update history",
    blurb: "Who changed what, by which role, with timestamps.",
  },
];

export default function AdminReports() {
  const { workspace, loading: wsLoading } = useAdminWorkspace();
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(null);

  const downloadCsv = async (k) => {
    if (!workspace) return;
    setBusy(k);
    try {
      const resp = await api.get(
        `/admin/workspaces/${workspace.id}/reports/${k}.csv?days=${days}`,
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${k}_${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setBusy(null);
    }
  };

  if (wsLoading) return null;
  if (!workspace)
    return (
      <AdminEmpty testid="admin-reports-no-ws">
        No workspace yet.
      </AdminEmpty>
    );

  return (
    <div data-testid="admin-reports-screen">
      <AdminHeader eyebrow="Reports" title="Exports for your records" />

      <div className="mt-6 inline-flex items-center gap-3 bg-white border hairline rounded-full px-4 py-2">
        <span className="text-[11px] tracking-[0.22em] uppercase text-[#6B7280]">
          Window
        </span>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          data-testid="admin-reports-days"
          className="bg-transparent outline-none text-sm tabular-nums"
        >
          <option value={1}>1 day</option>
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days</option>
          <option value={60}>60 days</option>
          <option value={90}>90 days</option>
        </select>
      </div>

      <ul
        data-testid="admin-reports-list"
        className="mt-6 grid sm:grid-cols-2 gap-4"
      >
        {KINDS.map((k) => (
          <li
            key={k.kind}
            data-testid={`admin-report-${k.kind}`}
            className="rounded-3xl bg-white border hairline p-6 flex items-start gap-4"
          >
            <span
              className="h-11 w-11 rounded-2xl bg-[#F7F8FC] flex items-center justify-center shrink-0"
              aria-hidden
            >
              <FileText size={20} strokeWidth={1.6} className="text-[#1A1B2E]" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-lg text-[#1A1B2E]">{k.title}</h3>
              <p className="text-sm text-[#6B7280] mt-1">{k.blurb}</p>
              <button
                type="button"
                onClick={() => downloadCsv(k.kind)}
                disabled={busy === k.kind}
                data-testid={`admin-report-${k.kind}-download`}
                className="mt-4 btn-ghost !py-1.5 !px-3 !text-[10px]"
              >
                <Download size={11} strokeWidth={1.5} />
                {busy === k.kind ? "Preparing…" : "Download CSV"}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
